import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import introVideo from '@/assets/link-intro-video.mp4';
import LoadingOverlay from './LoadingOverlay';

interface GenerationOverlayProps {
  show: boolean;
}

/* ─── Intro Video Phase ─── */
function IntroVideo({ onEnded }: { onEnded: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    videoRef.current?.play().catch(() => {
      onEnded();
    });
  }, [onEnded]);

  return (
    <motion.div
      className="absolute inset-0 z-20 flex items-center justify-center bg-black"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: 'easeInOut' }}
    >
      <video
        ref={videoRef}
        src={introVideo}
        muted
        playsInline
        onEnded={onEnded}
        className="w-full h-full object-contain"
        style={{ maxWidth: '100vw', maxHeight: '100vh' }}
      />
    </motion.div>
  );
}

/* ─── Main Overlay ─── */
export function GenerationOverlay({ show }: GenerationOverlayProps) {
  const [videoFinished, setVideoFinished] = useState(false);

  useEffect(() => {
    if (show) {
      setVideoFinished(false);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Intro video phase */}
      <AnimatePresence>
        {!videoFinished && (
          <IntroVideo onEnded={() => setVideoFinished(true)} />
        )}
      </AnimatePresence>

      {/* Main loading phase — white background with animated circuit logo */}
      <LoadingOverlay show={true} />
    </div>
  );
}
